/**
 * src/hooks/useCoachSubscription.ts
 * 
 * Custom hook for managing coach subscription status.
 * Handles fetching subscription status and determining access permissions.
 * Created to control dashboard access based on coach subscription status.
 */

import { useCallback, useEffect, useState } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { supabase } from '../lib/supabase';

export type CoachSubscriptionStatus = 'pending' | 'active' | 'suspended' | 'cancelled' | 'none' | 'loading';

export interface CoachSubscription {
  id: string;
  coach_id: string;
  status: CoachSubscriptionStatus;
  created_at: string;
  updated_at: string;
}

export interface UseCoachSubscriptionReturn {
  subscription: CoachSubscription | null;
  status: CoachSubscriptionStatus;
  isLoading: boolean;
  error: string | null;
  hasActiveSubscription: boolean;
  canAccessDashboard: boolean;
  refreshSubscription: () => Promise<void>;
}

export function useCoachSubscription(): UseCoachSubscriptionReturn {
  const { profile } = useProfile();
  const [subscription, setSubscription] = useState<CoachSubscription | null>(null);
  const [status, setStatus] = useState<CoachSubscriptionStatus>('loading');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!profile || profile.role !== 'coach') {
      setSubscription(null);
      setStatus('none');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // First try to get subscription data directly
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('coach_subscriptions')
        .select('*')
        .eq('coach_id', profile.id)
        .maybeSingle();

      if (subscriptionError) {
        console.error('Error fetching subscription:', subscriptionError);
        
        // Fallback to using the RPC function
        const { data: statusData, error: statusError } = await supabase
          .rpc('get_coach_subscription_status', { coach_uuid: profile.id });

        if (statusError) {
          throw statusError;
        }

        setStatus(statusData as CoachSubscriptionStatus || 'none');
        setSubscription(null);
      } else {
        setSubscription(subscriptionData);
        setStatus(subscriptionData?.status as CoachSubscriptionStatus || 'none');
      }
    } catch (err) {
      console.error('Error in fetchSubscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription status');
      setStatus('none');
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [profile]);

  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Set up real-time subscription to subscription changes
  useEffect(() => {
    if (!profile || profile.role !== 'coach') return;

    const subscription = supabase
      .channel('coach_subscription_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'coach_subscriptions',
          filter: `coach_id=eq.${profile.id}`,
        },
        (payload) => {
          console.log('Coach subscription changed:', payload);
          refreshSubscription();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [profile, refreshSubscription]);

  const hasActiveSubscription = status === 'active';
  const canAccessDashboard = hasActiveSubscription;

  return {
    subscription,
    status,
    isLoading,
    error,
    hasActiveSubscription,
    canAccessDashboard,
    refreshSubscription,
  };
} 